import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { HeartIcon, MessageCircleIcon, PauseIcon, PlayIcon, SendIcon, Volume2Icon, VolumeXIcon } from 'lucide-react';
import { brand, reelClips } from '../data/content';
const CLIP_MS = 4200;
export function HeroReel() {
  const reduceMotion = useReducedMotion();
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [muted, setMuted] = useState(true);
  const [liked, setLiked] = useState(false);
  const [progress, setProgress] = useState(0);
  const startedAt = useRef<number>(0);
  const clip = reelClips[index];
  useEffect(() => {
    if (!playing) return;
    let frame = 0;
    startedAt.current = performance.now() - progress * CLIP_MS;
    const tick = (now: number) => {
      const ratio = Math.min((now - startedAt.current) / CLIP_MS, 1);
      setProgress(ratio);
      if (ratio >= 1) {
        setProgress(0);
        setIndex(i => (i + 1) % reelClips.length);
        return;
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing, index]);
  const goTo = (i: number) => {
    setProgress(0);
    setIndex(i);
  };
  return <div className="relative mx-auto h-full w-full" data-id="element-142" data-mp="3m753s694v43z">
      <div className="pearl-surface absolute -inset-2 -z-10 rounded-[2.25rem] border border-butter-100 dark:border-butter-700/40 sm:-inset-3 sm:rounded-[2.75rem]" data-id="element-143" data-mp="doz8to8960em" />

      <div className="relative h-full min-h-[15rem] w-full overflow-hidden rounded-[1.5rem] bg-slate-900 sm:rounded-[2rem]" data-id="element-144" data-mp="3zflo6dibkdy">
        <AnimatePresence initial={false} data-id="element-145" data-mp="8xpjkoqm8z39">
          <motion.img key={clip.id} src={clip.src} alt={clip.alt} initial={{
          opacity: 0,
          scale: reduceMotion ? 1 : 1.08
        }} animate={{
          opacity: 1,
          scale: reduceMotion ? 1 : 1
        }} exit={{
          opacity: 0
        }} transition={{
          opacity: {
            duration: 0.5
          },
          scale: {
            duration: CLIP_MS / 1000 + 1,
            ease: 'linear'
          }
        }} className="absolute inset-0 h-full w-full object-cover" loading="eager" data-id="element-146" data-mp="8wo9amhfcgml" />
        </AnimatePresence>

        {/* Legibility scrim */}
        <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-t from-slate-950/85 via-slate-950/10 to-slate-950/45" data-id="element-147" data-mp="wd00a28upawd" />

        {/* Progress bars */}
        <div className="absolute inset-x-2.5 top-2.5 flex gap-1" data-id="element-148" data-mp="nkxy9zj4vmjz">
          {reelClips.map((c, i) => <button key={c.id} type="button" onClick={() => goTo(i)} aria-label={`Play clip ${i + 1}: ${c.caption}`} className="group h-1 flex-1 rounded-full bg-white/30" data-id="element-149" data-mp="illejnnk6vlm" data-mpi={i}>
              <span className="block h-full rounded-full bg-butter-300 transition-[width] duration-100 ease-linear" style={{
            width: i < index ? '100%' : i === index ? `${progress * 100}%` : '0%'
          }} data-id="element-150" data-mp="dl7644i1pjn2" />
            </button>)}
        </div>

        {/* Account row */}
        <div className="absolute inset-x-2.5 top-7 flex items-center gap-1.5" data-id="element-151" data-mp="2gbrthxet0ef4">
          <span className="pearl-surface flex h-5 w-5 flex-none items-center justify-center rounded-full border border-butter-200/70" data-id="element-152" data-mp="2k9plt93vfgmq">
            <span className="font-display text-[9px] font-semibold text-slate-800" data-id="element-153" data-mp="2u9gfj8klxrlr">ee</span>
          </span>
          <span className="truncate text-[10px] font-semibold text-white drop-shadow" data-id="element-154" data-mp="1wlzmm17965fy">
            {brand.name}
          </span>
        </div>

        {/* Side actions */}
        <div className="absolute bottom-20 right-2 flex flex-col items-center gap-3" data-id="element-155" data-mp="21iq3gqqvt52f">
          <button type="button" onClick={() => setLiked(v => !v)} aria-pressed={liked} aria-label={liked ? 'Unlike this clip' : 'Like this clip'} className="flex flex-col items-center gap-0.5 text-white transition-transform duration-200 hover:scale-110" data-id="element-156" data-mp="26iks0nn6pikb">
            <HeartIcon className={`h-4 w-4 drop-shadow ${liked ? 'fill-butter-300 text-butter-300' : ''}`} data-id="element-157" data-mp="1itrvoq2nh8p6" />
            <span className="text-[8px] font-semibold drop-shadow" data-id="element-158" data-mp="3kip85829wb6b">Like</span>
          </button>
          <span className="flex flex-col items-center gap-0.5 text-white" data-id="element-159" data-mp="3gionh5okz3qt">
            <MessageCircleIcon className="h-4 w-4 drop-shadow" aria-hidden="true" data-id="element-160" data-mp="3u90fz5p2v4ih" />
            <span className="text-[8px] font-semibold drop-shadow" data-id="element-161" data-mp="3ub4pq49nqxqo">Comment</span>
          </span>
          <span className="flex flex-col items-center gap-0.5 text-white" data-id="element-162" data-mp="3pgqsdwbfqgjd">
            <SendIcon className="h-4 w-4 drop-shadow" aria-hidden="true" data-id="element-163" data-mp="808ymozjb8qh" />
            <span className="text-[8px] font-semibold drop-shadow" data-id="element-164" data-mp="31xr4kwppabf">Share</span>
          </span>
        </div>

        {/* Caption */}
        <div className="absolute inset-x-2.5 bottom-3" data-id="element-165" data-mp="h0nw0mprweac">
          <AnimatePresence mode="wait" initial={false} data-id="element-166" data-mp="c4qgujq8xdz5">
            <motion.p key={clip.id} initial={{
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0
          }} transition={{
            duration: 0.3
          }} className="max-w-[9.5rem] text-[11px] font-semibold leading-snug text-white drop-shadow" data-id="element-167" data-mp="c1uemj7d834w">
              {clip.caption}
            </motion.p>
          </AnimatePresence>

          <div className="mt-2.5 flex items-center gap-1.5" data-id="element-168" data-mp="klhcjvfioae2">
            <button type="button" onClick={() => setPlaying(v => !v)} aria-label={playing ? 'Pause reel' : 'Play reel'} className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-white/20 text-white backdrop-blur transition-colors hover:bg-white/30" data-id="element-169" data-mp="kiv31q92fkt7">
              {playing ? <PauseIcon className="h-3 w-3" data-id="element-170" data-mp="fmwbjtsfxmok" /> : <PlayIcon className="h-3 w-3" data-id="element-171" data-mp="1m3cqwv3w5fn" />}
            </button>
            <button type="button" onClick={() => setMuted(v => !v)} aria-label={muted ? 'Unmute reel' : 'Mute reel'} className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-white/20 text-white backdrop-blur transition-colors hover:bg-white/30" data-id="element-172" data-mp="1nwm4jx3t3rc">
              {muted ? <VolumeXIcon className="h-3 w-3" data-id="element-173" data-mp="20052w2yusy47" /> : <Volume2Icon className="h-3 w-3" data-id="element-174" data-mp="1zz3q9u26w3kd" />}
            </button>
          </div>
        </div>
      </div>
    </div>;
}